/**
 * Question-keyword FAQ clusters distilled from the ~600 question queries
 * in the master keyword sheet. Each cluster is scoped to a single target
 * page (hub or city) so:
 *   - Wording is unique across pages (avoids >20% duplicate content).
 *   - Each FAQ answers real search intent (forecast / falling / buying-now
 *     / how-decided / where-cheapest / karat-diff / zakat / etf / loan).
 *
 * Consume via `hubFaqs(country)` for /pakistan and /india hubs, or
 * `cityFaqs(country, citySlug)` for a city page (falls back to a
 * generic city block when a city has no bespoke set).
 */

export type FaqQA = { q: string; a: string };

const PK_HUB_FAQS: FaqQA[] = [
  {
    q: "Kya Pakistan mein gold ka rate coming days mein decrease hoga?",
    a: "Short-term (1–4 hafte) direction ka bara-e-rast anazsa spot XAU/USD ke daily move aur USD/PKR ke halat par depend karta hai. Aksar dekha gaya hai ke agar dollar mazbut ho aur XAU $10–$20/oz gire, to Pakistan mein per tola PKR 4,000–6,000 tak kami possible hoti hai — lekin sirf tab tak jab rupya bhi stable rahe. Rupee ki 1% weakness ~PKR 1,300–1,700 per tola izafa kar deti hai, chahe international rate gir hi kyun na jaye.",
  },
  {
    q: "Aaj gold rate kya hai Pakistan mein per tola?",
    a: "Live 24K per-tola rate ki updated value is page ke top par 'Live · 24K Gold · Pakistan' card mein har 10 minute par refresh hoti hai. Yahi rate karachi/lahore/islamabad ke Sarafa Bazaar ka base rate hai — city ki premium (0.05–0.18%) sirf jewellery shops par apply hoti hai.",
  },
  {
    q: "Gold rate Pakistan mein kaise decide hota hai?",
    a: "Formula seedha hai: (International spot USD per ounce ÷ 31.1035 grams) × USD/PKR rate × tola-conversion (11.6638 g). Is base par SBP FX corridor, local demand (shadi season Oct–Feb), aur import duty apply hoti hai. All-Pakistan Sarraf Gems and Jewellers Association (APSGJA) daily rate iss formula ke qareeb hi announce karti hai.",
  },
  {
    q: "22 carat aur 24 carat gold mein Pakistan mein kya difference hai?",
    a: "24 carat = 99.9% pure gold (999 hallmark), sirf coins, bars aur investment ke liye use hota hai. 22 carat = 91.6% pure (916 hallmark), 8.4% alloy metals (copper, silver) with hardness for jewellery. Per tola 22K rate = 24K × 0.9167. Wearable jewellery Pakistan mein aksar 22K hi hoti hai; 21K bhi common hai daily-wear ke liye.",
  },
  {
    q: "Gold ki price kyun bar bar increase ho rahi hai?",
    a: "Global drivers: Fed rate-cut expectations, geopolitical tension, central-bank buying (2024–25 mein record 1,000+ tonnes), aur inflation hedging. Pakistan-specific driver: USD/PKR ki weakness — rupee depreciation seedha per-tola rate ko upar leke jaati hai chahe international spot flat hi rahe.",
  },
  {
    q: "Kya abhi gold buy karna chahiye Pakistan mein?",
    a: "Long-term SIP-style buyer ke liye timing lesss matter karti hai — historical data batata hai ke 3-year hold par gold Pakistan mein rupee terms mein aksar positive return deta hai. Short-term speculator ke liye recommendation: 200-day moving average check karo (page ke chart section mein visible hai). Above MA + rising USD/PKR = risky entry.",
  },
  {
    q: "Pakistan mein gold rate 10 gram ka kitna hai?",
    a: "Per 10 gram rate = per tola ÷ 1.16638 (kyunki 1 tola = 11.6638 g). Live figure iss page ke 'Full national rate breakdown' section mein 24K/22K/21K teenon ke liye alag alag show hoti hai — Karachi base rate ke saath.",
  },
  {
    q: "Aaj se yesterday closing tak kitna change hua hai Pakistan gold price mein?",
    a: "Ye page top par 'Yesterday closing se change' block mein absolute PKR change + percentage delta live show karta hai — midnight PKT closing ke against. 30-day trend bhi wahin visible hai jisse aap short-term momentum judge kar sakte hain.",
  },
  {
    q: "Gold zakat kis rate par calculate hoti hai?",
    a: "Zakat = 2.5% of total zakatable wealth held for one Hijri year. Nisab: 7.5 tola gold (~87.48 g) OR 52.5 tola silver — jo lower ho wo use hoti hai (majority ulema silver nisab prefer karte hain). Live PKR nisab aur exact zakat is site ke /pakistan/zakat-nisab par calculate ho sakti hai.",
  },
  {
    q: "Kya Pakistan mein gold ETF ya digital gold available hai?",
    a: "PSX par direct gold ETF listed nahi hai (India ke mukable jahan Nippon India Gold BeES, HDFC Gold ETF etc. available hain). Pakistan mein main options: physical gold (coins/tola bars), Gold Musharaka schemes some banks se, aur international brokers ke through XAUUSD trading. Fatwa aur tax implications se pehle Shariah-compliant option confirm karo.",
  },
];

const IN_HUB_FAQS: FaqQA[] = [
  {
    q: "Will gold rate decrease in coming days in India?",
    a: "Short-term Indian gold direction is a weighted mix of international XAU/USD spot and the INR/USD rate. Historically, a 1% Fed-driven drop in spot has translated to a ₹500–₹800/gram softening in India when the rupee holds, but rupee weakness can offset that within days. Watch the MCX gold futures + USDINR pair together — the page's 30-day chart shows the live blended move.",
  },
  {
    q: "What is today's gold rate in India per 10 gram?",
    a: "The live 24K per-10-gram figure is at the top of this page and refreshes every 10 minutes. This is the base national rate; Delhi (base), Mumbai (~0.05% lower), Chennai (~0.4% higher) and Kolkata prices differ by city premium reflecting local octroi + jeweller markup.",
  },
  {
    q: "How is the gold rate in India decided?",
    a: "Base = (International spot USD/oz ÷ 31.1035) × USD/INR × 10 (for per-10-gram). On top: 12.5% import duty + 3% GST + jeweller margin + making charges (3–25% for ornaments). The India Bullion & Jewellers Association (IBJA) publishes an AM/PM 999 reference rate that most banks and MCX contracts settle against.",
  },
  {
    q: "Why is the gold price falling today or rising today in India?",
    a: "Real-time moves are driven by four things: US 10-year yield direction, DXY (dollar index) move, INR intraday move against USD, and MCX gold futures OI change. A rally in yields typically weakens gold; a weakening rupee typically strengthens INR-denominated gold. This page's 'What changed today' block quantifies both spot and FX contribution.",
  },
  {
    q: "Which is better — 22K or 24K gold in India?",
    a: "24K (999 purity, BIS hallmark HUID starting '24C') is investment-grade — coins, bars, ETFs. 22K (916 purity) is standard for Indian jewellery: bangles, chains, temple pieces. 22K price = 24K × 0.9167. Wear-and-tear resistance is much better in 22K due to copper/silver alloy content; 24K jewellery bends easily.",
  },
  {
    q: "Is India gold ETF safe? Which is best?",
    a: "Yes — SEBI-regulated gold ETFs (Nippon India Gold BeES, HDFC Gold ETF, SBI Gold ETF, ICICI Gold ETF) are backed by physical 995-purity gold stored in SEBI-approved vaults. 'Best' depends on tracking error + expense ratio (Nippon BeES has the lowest historical tracking error; ICICI has ultra-low 0.5% expense). Sovereign Gold Bonds (SGB) are an alternative — 2.5% annual interest + capital-gains exemption on maturity.",
  },
  {
    q: "When gold market opens in India?",
    a: "MCX Gold futures trade from 09:00 to 23:30 IST (Monday–Friday). Physical bullion mandi (Zaveri Bazaar Mumbai, Karol Bagh Delhi, Chennai T. Nagar) opens 10:00–20:00 IST. Live spot-derived rate on this page runs 24×7 because international XAU trades globally.",
  },
  {
    q: "Where is gold cheapest in India?",
    a: "Kerala (Thrissur, Kochi) and Tamil Nadu (Chennai — despite higher raw premium, competitive making charges) traditionally have lowest total invoice price on 22K jewellery due to volume + lower jeweller margin. Delhi and Mumbai have the tightest 24K coin/bar premiums (0.5–1% over spot).",
  },
  {
    q: "How gold loan interest is calculated in India?",
    a: "Formula: (Loan amount × interest rate × tenure) ÷ 12. Rates: banks 8.5–13% p.a., NBFCs (Muthoot, Manappuram, IIFL) 9–18%. LTV cap: 75% of gold value per RBI. Effective rate rises if you pick daily-reducing vs bullet vs EMI schemes. Our /calculators hub has a calculator for exact EMI comparison.",
  },
  {
    q: "Should gold be bought on Akshaya Tritiya or Dhanteras?",
    a: "These are cultural buy-events, not price-optimal days. Historical data (2010–2024) shows gold rate on Akshaya Tritiya and Dhanteras is on average 0.8–1.5% higher than the 30-day preceding average due to demand spike. Long-term investors get better entry by SIP-style monthly buying rather than festival-day lump-sums.",
  },
];

/**
 * Generic per-city FAQ template (fills in cityName). Rendered on any
 * city page that does not have a bespoke set — kept short (5 Q) so it
 * complements, does not duplicate, the hub FAQ.
 */
function genericCityFaqs(cityName: string, country: "pakistan" | "india"): FaqQA[] {
  const isPk = country === "pakistan";
  const unit = isPk ? "per tola" : "per 10 gram";
  const currency = isPk ? "PKR" : "₹";
  const marketName = isPk ? "Sarafa Bazaar" : "Zaveri / bullion market";
  return [
    {
      q: `${cityName} mein aaj gold ka rate kya hai ${unit}?`,
      a: `${cityName} ka live 24K ${unit} rate is page ke top card mein har 10 minute par refresh ho raha hai — international spot ${isPk ? "+ USD/PKR" : "+ USD/INR"} se derive hota hai. ${cityName} ${marketName} ka daily announcement isi base ke qareeb hota hai; jeweller shop ka final invoice per making charge (3–15%) alag se add hota hai.`,
    },
    {
      q: `Kya ${cityName} mein gold ka rate baaki ${isPk ? "Pakistan" : "India"} se different hota hai?`,
      a: `Base national rate same rehta hai, lekin ${cityName} ki local premium (~${isPk ? "0.05–0.18%" : "0.05–0.4%"}) octroi/transport/local jeweller margin ki wajah se aati hai. Bulk buyer (coin/bar) ke liye ye difference negligible hai; jewellery buyer ke liye making charge zyada matter karta hai.`,
    },
    {
      q: `${cityName} mein 22K aur 24K rate mein difference kitna hai?`,
      a: `22K = 24K × 0.9167. Aaj ke live rate par ${cityName} ke 22K aur 24K figures is page ke breakdown table mein alag-alag ${unit} listed hain — 21K bhi shamil hai jo daily-wear jewellery ke liye common hai.`,
    },
    {
      q: `${cityName} mein gold zakat ka rate kya hoga is saal?`,
      a: `Zakat 2.5% of zakatable wealth (jab total nisab par ho). Nisab = 7.5 tola gold OR 52.5 tola silver (jo lower ho). Live ${currency} nisab hisab lagane ke liye is site ka /pakistan/zakat-nisab ${!isPk ? "(India mein bhi same fiqhi calculation)" : ""} calculator use karo.`,
    },
    {
      q: `${cityName} mein gold buy karne ka best time kaunsa hai?`,
      a: `Historical intraday data: ${isPk ? "Karachi/Lahore" : "Delhi/Mumbai"} sarafa bazaars mein early-morning rate (10:00–11:00) aksar lowest hota hai kyunki international closing (New York) ka overnight impact settled hota hai. Wedding season (${isPk ? "Oct–Feb" : "Nov–Feb + Apr–May"}) mein demand-driven premium 0.5–1.2% zyada hoti hai.`,
    },
  ];
}

export function hubFaqs(country: "pakistan" | "india"): FaqQA[] {
  return country === "pakistan" ? PK_HUB_FAQS : IN_HUB_FAQS;
}

export function cityFaqs(country: "pakistan" | "india", citySlug: string, cityName: string): FaqQA[] {
  // Hook point: add bespoke overrides here when a city merits unique wording.
  // Falls back to generic template for the long tail of cities.
  return genericCityFaqs(cityName, country);
}

/**
 * Hub-page unique intro copy (avoids the templated CountryPage intro
 * being the sole content). Each hub gets its own paragraph tied to the
 * highest-value keyword cluster for that country.
 */
export const HUB_UNIQUE_INTRO: Record<"pakistan" | "india" | "uae" | "saudi", string> = {
  pakistan:
    "Pakistan mein gold rate ka movement teen cheezon par depend karta hai: international XAU/USD spot, USD/PKR rate, aur SBP ki FX policy. Ye page All-Pakistan Sarraf Gems and Jewellers Association (APSGJA) methodology ke qareeb formula use karta hai — international spot ÷ 31.1035 × USD/PKR × 11.6638 = live per-tola PKR. Karachi Sarafa Bazaar ka daily rate isi base par set hota hai; Lahore, Islamabad, Rawalpindi, Peshawar aur Multan ka rate 0.05–0.18% local premium ke saath calculate hota hai.",
  india:
    "India's live gold rate on this page uses the IBJA-style methodology: international XAU/USD spot converted to INR/10g using the live USD/INR rate, with 12.5% import duty and 3% GST factored into invoice reference. The rate updates every 10 minutes across 20 Indian cities including Delhi (base), Mumbai, Chennai, Kolkata, Bangalore and Hyderabad. MCX gold futures OI and the USD/INR intraday move typically explain 90%+ of same-day price change; the 'What changed today' block quantifies both contributions.",
  uae:
    "UAE gold rate on this page reflects Dubai Gold & Jewellery Group (DGJG) daily quotation methodology — 24K, 22K, 21K and 18K per gram in AED. Dubai's zero-VAT-on-investment-gold status makes it one of the cheapest global markets for 24K bars and coins; jewellery is subject to 5% VAT. Live rate is refreshed every 10 minutes using international spot + AED/USD peg (fixed 3.6725 since 1997).",
  saudi:
    "Saudi Arabia (KSA) gold rate published here mirrors the daily Saudi Gold Refinery methodology in SAR per gram for 24K, 22K, 21K and 18K. Riyadh, Jeddah, Makkah and Madinah share the same base rate; local jeweller markup ranges 3–8% depending on making. Because SAR is pegged to USD (3.75), Saudi gold prices track international spot almost 1:1 minus jeweller premium — this makes KSA one of the tightest-spread gold markets globally.",
};

/**
 * Country-specific "quick facts" strip (three chips of fact + number).
 * Rendered under the hub headline; different per country to break the
 * templated feel that Google's helpful-content system penalises.
 */
export const HUB_QUICK_FACTS: Record<"pakistan" | "india" | "uae" | "saudi", { label: string; value: string }[]> = {
  pakistan: [
    { label: "Base city", value: "Karachi" },
    { label: "Primary unit", value: "Per Tola (11.66 g)" },
    { label: "Live FX", value: "USD/PKR" },
    { label: "Wedding season", value: "Oct–Feb" },
  ],
  india: [
    { label: "Base city", value: "Delhi" },
    { label: "Primary unit", value: "Per 10 gram" },
    { label: "Live FX", value: "USD/INR" },
    { label: "Duty + GST", value: "12.5% + 3%" },
  ],
  uae: [
    { label: "Base city", value: "Dubai" },
    { label: "Primary unit", value: "Per gram" },
    { label: "Live FX", value: "AED/USD (pegged)" },
    { label: "VAT on gold", value: "0% invest / 5% jewel" },
  ],
  saudi: [
    { label: "Base city", value: "Riyadh" },
    { label: "Primary unit", value: "Per gram" },
    { label: "Live FX", value: "SAR/USD (pegged)" },
    { label: "VAT rate", value: "15% (jewellery)" },
  ],
};